import React, { useState } from "react";
import { formatCompact } from "../StatTile";
import "./AgingDonut.css";

const SIZE = 120;
const CENTER = SIZE / 2;
const RADIUS = 46;
const STROKE = 20;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// Donut slices are sized by $ amount (the framing that matters to the
// business — "how much money is stuck where"), falling back to count when
// every bucket's amount is zero so the ring never renders blank on live data.
// Segments are separate SVG <circle> strokes (not a single conic-gradient)
// so each one is independently hoverable — hovering a slice (or its legend
// row) swaps the center readout to that bucket's own count/amount/share.
const AgingDonut = ({ buckets, data, totalCount, totalAmount }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const amounts = buckets.map((b) => Number(data?.[b.key]?.amount) || 0);
  const counts = buckets.map((b) => Number(data?.[b.key]?.count) || 0);
  const sumAmount = amounts.reduce((a, v) => a + v, 0);
  const basis = sumAmount > 0 ? amounts : counts;
  const sumBasis = basis.reduce((a, v) => a + v, 0);

  let cumulative = 0;
  const segments = buckets.map((b, i) => {
    const fraction = sumBasis > 0 ? basis[i] / sumBasis : 0;
    const length = fraction * CIRCUMFERENCE;
    const offset = CIRCUMFERENCE - cumulative;
    cumulative += length;
    return { ...b, fraction, length, offset };
  });

  const hovered = hoveredIdx !== null ? segments[hoveredIdx] : null;
  const holeValue = hovered ? formatCompact(amounts[hoveredIdx], { currency: true }) : formatCompact(totalAmount, { currency: true });
  const holeLabel = hovered
    ? `${hovered.label} · ${Math.round(hovered.fraction * 100)}%`
    : `${totalCount} item${totalCount === 1 ? "" : "s"}`;

  return (
    <div className="aging-donut-card">
      <div className="aging-donut-ring-wrap">
        <svg
          className="aging-donut-svg"
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          width={SIZE}
          height={SIZE}
          onMouseLeave={() => setHoveredIdx(null)}
        >
          <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
            {sumBasis > 0 ? (
              segments.map((seg, i) => (
                <circle
                  key={seg.key}
                  cx={CENTER}
                  cy={CENTER}
                  r={RADIUS}
                  fill="none"
                  stroke={seg.color}
                  strokeWidth={hoveredIdx === i ? STROKE + 4 : STROKE}
                  strokeDasharray={`${seg.length} ${CIRCUMFERENCE - seg.length}`}
                  strokeDashoffset={seg.offset}
                  opacity={hoveredIdx === null || hoveredIdx === i ? 1 : 0.35}
                  className="aging-donut-segment"
                  onMouseEnter={() => setHoveredIdx(i)}
                />
              ))
            ) : (
              <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" className="aging-donut-empty-ring" strokeWidth={STROKE} />
            )}
          </g>
        </svg>

        <div className="aging-donut-hole">
          <span className="aging-donut-total-value">{holeValue}</span>
          <span className="aging-donut-total-label">{holeLabel}</span>
        </div>
      </div>

      <ul className="aging-donut-legend">
        {buckets.map((b, i) => {
          const pct = sumBasis > 0 ? Math.round((basis[i] / sumBasis) * 100) : 0;
          return (
            <li
              key={b.key}
              className={`aging-donut-legend-row${hoveredIdx === i ? " aging-donut-legend-row-active" : ""}`}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              <span className="aging-donut-legend-swatch" style={{ background: b.color }} />
              <span className="aging-donut-legend-label">{b.label}</span>
              <span className="aging-donut-legend-amount">{formatCompact(amounts[i], { currency: true })}</span>
              <span className="aging-donut-legend-pct">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AgingDonut;
